import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../common/toast/hooks/useToast'
import { Currency, CurrencyFormProps } from './Currencies.d'
import { Card } from 'primereact/card'
import { api } from '../../../../auth'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col, Form } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import PageHeader from '../../../layout/page/PageHeader'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import TextField from '../../../common/forms/fields/TextField'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCircleNotch,
    faHandPointRight,
} from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../../common/forms/errors/RenderFormikErrors'

const CurrencyForm: FC<CurrencyFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
}) => {
    const isViewOrEdit = id !== null
    const theme = useAppSelector((state) => state.theme.theme)

    const navigate = useNavigate()
    const axiosPrivate = api

    const emptyCurrency: Currency = {
        name: '',
        code: '',
        symbol: '',
        default: false,
        active: false,
    }

    const CurrencyFormSchema = Yup.object().shape({
        name: Yup.string().trim().required('Enter currency name'),
        code: Yup.string()
            .trim()
            .min(3, 'Code must be 3 characters only')
            .max(3, 'Code must be 3 characters only')
            .required('Enter unique code'),
        symbol: Yup.string().trim().required('Enter currency symbol'),
    })

    const [currencyDialog, setCurrencyDialog] = useState(true)
    const [currency, setCurrency] = useState<Currency>(emptyCurrency)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/administration/currencies/view/' + id
            : '/administration/currencies/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                if (response.data.data?.currency) {
                    setCurrency(response.data.data.currency)
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load currency')
            }
        }
    }, [axiosPrivate, id, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record: any) => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            const routePath = isViewOrEdit
                ? '/administration/currencies/update/' + id
                : '/administration/currencies/create'
            const response = await axiosPrivate
                .post(routePath, record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields as any, config as any)
        }
    }

    const hideDialog = () => {
        setCurrencyDialog(false)
        navigate('/administration/currencies')
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/administration/currencies/delete/' + id}
                    record={currency}
                    deletedCallback={() => hideDialog()}
                    btnLabel="Delete"
                    showIcon={false}
                    message={
                        <>
                            <p className="mb-3 pb-3 border-bottom">
                                Are you sure you want to delete currency:
                                <strong> {currency.name}</strong>?
                            </p>
                            <Alert variant="warning" className="border-0">
                                <strong>Warning!</strong> All codes related to
                                this currency will be deleted permenantly!
                            </Alert>
                        </>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'currency-form-submit-btn'
                    )
                    formSubmitGtn?.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={{
                    ...currency,
                }}
                validationSchema={CurrencyFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps: any) => {
                    return (
                        <FormikForm id="currencies-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col sm={12}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col xl={6}>
                                                <TextField
                                                    id="formCurrencyName"
                                                    name="name"
                                                    label="Name"
                                                    placeholder="Enter currency name"
                                                    formikProps={formikProps}
                                                    required
                                                />
                                            </Col>
                                            <Col xl={3}>
                                                <TextField
                                                    id="formCurrencyCode"
                                                    name="code"
                                                    label="Code (3 characters)"
                                                    placeholder="3 letters only"
                                                    formikProps={formikProps}
                                                    required
                                                />
                                            </Col>
                                            <Col xl={3}>
                                                <TextField
                                                    id="formCurrencySymbol"
                                                    name="symbol"
                                                    label="Symbol"
                                                    placeholder="i.e £, $"
                                                    formikProps={formikProps}
                                                    required
                                                />
                                            </Col>
                                        </Row>
                                    </fieldset>
                                    <fieldset>
                                        <legend>Status</legend>
                                        <Row>
                                            <Col xl={4}>
                                                <Form.Group
                                                    controlId={
                                                        'formCurrencyActiveFlag'
                                                    }
                                                >
                                                    <Form.Switch
                                                        type="checkbox"
                                                        label="Active"
                                                        name="active"
                                                        checked={
                                                            formikProps.values
                                                                .active
                                                        }
                                                        onChange={(e) =>
                                                            formikProps.setFieldValue(
                                                                'active',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xl={4}>
                                                <Form.Group
                                                    controlId={
                                                        'formCurrencyDefaultFlag'
                                                    }
                                                >
                                                    <Form.Switch
                                                        type="checkbox"
                                                        label="Default"
                                                        name="default"
                                                        checked={
                                                            formikProps.values
                                                                .default
                                                        }
                                                        onChange={(e) =>
                                                            formikProps.setFieldValue(
                                                                'default',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xl={12}>
                                                <Alert
                                                    variant={
                                                        formikProps.values
                                                            .default
                                                            ? 'warning'
                                                            : 'info'
                                                    }
                                                    className={
                                                        (formikProps.values
                                                            .default
                                                            ? 'warning'
                                                            : 'info') +
                                                        ' mt-3 mb-1'
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faHandPointRight}
                                                        className="me-2"
                                                    />{' '}
                                                    When a currency is set to{' '}
                                                    <strong>default</strong> the
                                                    system will automatically
                                                    mark it as{' '}
                                                    <strong>Active</strong>
                                                </Alert>
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                            </Row>

                            <input
                                type="submit"
                                id="currency-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={currencyDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '60vw' }}
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : currency.name
                                : 'Create new currency'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : currency.name
                                : 'Create new currency'
                        }
                    />
                    {FormContent}
                    {formFooter}
                </>
            )}
        </>
    )
}

export default CurrencyForm
